import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  GraduationCap, CheckCircle, XCircle, Loader2, AlertTriangle,
  Upload, Home, Clock, FileText, Plus, X, Send
} from 'lucide-react';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import PublicUploadContinuation from '../../components/worker/PublicUploadContinuation';
import { createLatestWorkerRead } from '../../lib/latestWorkerRead';
import { assertUploadReceived, publicUploadErrorMessage, UNCONFIRMED_UPLOAD } from '../../lib/publicUploadJourney';

const API = API_BASE;

// Some mobile file pickers do not provide a reliable MIME type for PDFs.
// Validate the same concrete extensions that the public training uploader supports
// instead of silently discarding otherwise valid files.
const ALLOWED_CERTIFICATE_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'webp']);
function certificateSelectionError(file) {
  const name = String(file?.name || '');
  const extension = name.split('.').pop()?.toLowerCase();
  if (!extension || !ALLOWED_CERTIFICATE_EXTENSIONS.has(extension)) {
    return `${name || 'This file'} cannot be uploaded. Choose a PDF, PNG, JPG or WebP certificate.`;
  }
  if (file.size > 10 * 1024 * 1024) {
    return `${name} is too large. The maximum file size is 10 MB.`;
  }
  return '';
}


/**
 * TrainingUploadPage - Public page for employees to upload training certificates
 * 
 * Flow:
 * 1. Employee receives email with secure link
 * 2. Opens link (no login required)
 * 3. Uploads one or more training certificates
 * 4. Certificates enter extraction + review queue
 */
export default function TrainingUploadPage() {
  const { token: credential } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [receivedCount, setReceivedCount] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const [validationAttempt, setValidationAttempt] = useState(0);
  const requestGuard = useRef(createLatestWorkerRead());
  const uploadPending = useRef(false);

  useEffect(() => {
    const guard = requestGuard.current;
    const ticket = guard.begin();
    setLoading(true);
    setError(null);
    setPageData(null);
    setFiles([]);
    setNotes('');
    setSubmitted(false);
    setSubmitting(false);
    setUploadProgress(0);
    setReceivedCount(0);
    setUploadResult(null);
    setUploadError('');
    setSelectionError('');
    uploadPending.current = false;
    const fetchPageData = async () => {
      try {
        if (!credential) throw new Error('Missing upload link');
        const response = await axios.get(`${API}/public/validate-upload-token`, {
          params: credential.includes('.') ? { token: credential } : { short_code: credential },
          signal: ticket.signal,
          timeout: 25000,
        });
        if (!ticket.isCurrent()) return;
        if (response.data?.status !== 'valid') throw new Error('Upload link was not confirmed');
        setPageData({ ...response.data, employee_name: response.data.person_name });
      } catch (err) {
        if (!ticket.isCurrent()) return;
        const detail = publicUploadErrorMessage(err, 'We could not open your upload link. Please try again.');
        const normalised = detail.toLowerCase();
        if (err.response?.status === 410 || normalised.includes('expired')) {
          setError({ type: 'expired', message: 'This link has expired. Please contact your administrator for a new link.' });
        } else if (normalised.includes('invalid')) {
          setError({ type: 'invalid', message: 'This link is invalid or is no longer available.' });
        } else {
          setError({ type: 'error', message: detail });
        }
      } finally {
        if (ticket.isCurrent()) setLoading(false);
      }
    };
    fetchPageData();
    return () => guard.cancel();
  }, [credential, validationAttempt]);

  const handleFileChange = (e) => {
    if (uploadPending.current) return;
    const newFiles = Array.from(e.target.files || []);
    const rejected = [];
    const validFiles = newFiles.filter((file) => {
      const errorMessage = certificateSelectionError(file);
      if (errorMessage) rejected.push(errorMessage);
      return !errorMessage;
    });

    // Persistent inline feedback is vital on phones where toast notifications
    // can disappear before the person notices why Submit is still disabled.
    const feedback = rejected.join(' ');
    setSelectionError(feedback);
    if (feedback) toast.error(feedback);
    setFiles((previous) => [...previous, ...validFiles]);
    // Allow re-selecting exactly the same file after an invalid selection.
    if (e.target) e.target.value = '';
  };

  const removeFile = (index) => {
    if (uploadPending.current) return;
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploadPending.current) return;
    
    if (files.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    uploadPending.current = true;
    const ticket = requestGuard.current.begin();
    setSubmitting(true);
    setUploadProgress(0);
    setUploadError('');
    setSelectionError('');

    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const formData = new FormData();
        if (credential?.includes('.')) {
          formData.append('token', credential);
        } else {
          formData.append('short_code', credential);
        }
        formData.append('file', file);
        if (notes.trim()) {
          formData.append('notes', notes.trim());
        }

        const response = await axios.post(`${API}/public/upload-document`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: ticket.signal,
          timeout: 60000,
          onUploadProgress: (progressEvent) => {
            if (!ticket.isCurrent()) return;
            const filePercent = progressEvent.total
              ? progressEvent.loaded / progressEvent.total
              : 0;
            const overall = Math.round(((index + filePercent) / files.length) * 100);
            setUploadProgress(Math.min(99, overall));
          }
        });
        if (!ticket.isCurrent()) return;
        const receipt = assertUploadReceived(response.data);
        setUploadResult(receipt);
        setReceivedCount((count) => count + 1);
        // Keep only files without a confirmed receipt available for retry.
        setFiles((remaining) => remaining.filter((item) => item !== file));
      }

      if (!ticket.isCurrent()) return;
      setUploadProgress(100);
      setSubmitted(true);
      toast.success('Training certificates uploaded successfully!');
    } catch (err) {
      if (!ticket.isCurrent()) return;
      const detail = publicUploadErrorMessage(err, UNCONFIRMED_UPLOAD);
      setUploadError(detail);
      toast.error(detail);
    } finally {
      if (ticket.isCurrent()) {
        uploadPending.current = false;
        setSubmitting(false);
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary mb-4" />
            <p className="text-text-muted">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="p-8 text-center">
            {error.type === 'expired' ? (
              <Clock className="h-16 w-16 mx-auto text-amber-500 mb-4" />
            ) : (
              <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            )}
            <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
              {error.type === 'expired' ? 'Link Expired' : 'Unable to Load'}
            </h2>
            <p className="text-text-muted mb-6">{error.message}</p>
            {error.type === 'error' && credential && (
              <Button className="mb-4" onClick={() => setValidationAttempt((value) => value + 1)}>Try Again</Button>
            )}
            <Link to="/">
              <Button variant="outline" className="rounded-xl">
                <Home className="h-4 w-4 mr-2" />
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-heading font-semibold text-text-primary mb-2">
              Certificates received
            </h2>
            <p className="text-text-muted mb-6">
              Your training certificates have been submitted successfully. 
              The compliance team will review them shortly.
            </p>
            <div className="p-4 bg-gray-50 rounded-xl mb-6 text-sm text-text-muted">
              <p>{receivedCount} certificate(s) received and awaiting review</p>
            </div>
            <PublicUploadContinuation continuation={uploadResult?.portal_continuation} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main upload form
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <GraduationCap className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary mb-2">
            Training Certificate Upload
          </h1>
          {pageData?.employee_name && (
            <p className="text-text-muted">
              For: <span className="font-medium text-text-primary">{pageData.employee_name}</span>
            </p>
          )}
        </div>

        {/* Custom Message from Admin */}
        {pageData?.custom_message && (
          <Card className="mb-6 border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800 text-sm">Message from HR:</p>
                  <p className="text-blue-700 text-sm mt-1">{pageData.custom_message}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* The secure upload link is evidence-only: never offer provider checkout
            or booking links to a worker. The agency arranges mandatory courses. */}
        {pageData?.training_types?.length > 0 && (
          <details className="mb-6 rounded-xl border border-slate-200 bg-white" data-testid="public-training-requested-details">
            <summary className="cursor-pointer p-4 text-sm font-medium text-slate-800">
              Requested certificates ({pageData.training_types.length})
            </summary>
            <ul className="space-y-1 border-t border-slate-100 px-4 py-3 text-sm text-slate-700">
              {pageData.training_types.map((type, idx) => (
                <li key={idx}>{String(type).replace(/_/g, ' ').replace(/training$/, '').trim()}</li>
              ))}
            </ul>
          </details>
        )}
        <p className="mb-5 text-sm text-slate-700">
          If you need training, sign in to your organisation's CareDock portal and choose
          <strong> I need training</strong>. Your organisation will arrange it.
        </p>

        {/* Due Date */}
        {pageData?.due_date && (
          <div className="flex items-center justify-center gap-2 mb-6 text-sm text-text-muted">
            <Clock className="h-4 w-4" />
            <span>Please submit by: </span>
            <span className="font-medium text-text-primary">
              {new Date(pageData.due_date).toLocaleDateString('en-GB', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
        )}

        {/* Upload Form */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading">Upload Your Certificates</CardTitle>
            <CardDescription>
              Upload PDF or image files of your training certificates. You can upload multiple files.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {selectionError && (
                <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  {selectionError}
                </div>
              )}
              <p className="text-sm text-slate-700" role="status" aria-live="polite">
                {submitting
                  ? 'Uploading your certificate. Please keep this page open until you see a receipt.'
                  : files.length > 0
                    ? `${files.length} certificate${files.length === 1 ? '' : 's'} selected. Tap the Submit button below to send ${files.length === 1 ? 'it' : 'them'}.`
                    : 'First select a PDF or image file. Then tap Submit to send it securely.'}
              </p>
              {uploadError && (
                <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p>{uploadError}</p>
                  {receivedCount > 0 && <p className="mt-2">{receivedCount} certificate(s) received and awaiting review. The remaining files are still listed below.</p>}
                  <Link to="/access#applicant-access" referrerPolicy="no-referrer" className="mt-2 inline-block font-semibold underline">Open CareOps</Link>
                </div>
              )}
              {/* File Drop Zone */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Training Certificates *</Label>
                <div 
                  className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => { if (!submitting) document.getElementById('file-input')?.click(); }}
                  data-testid="file-drop-zone"
                >
                  <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-text-primary font-medium">Click to upload certificates</p>
                  <p className="text-sm text-text-muted mt-1">PDF, PNG, JPG up to 10MB each</p>
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    disabled={submitting}
                    accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>

                {/* Selected Files List */}
                {files.length > 0 && (
                  <div className="space-y-2">
                    {files.map((file, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-[#F8FAFA] rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-primary" />
                          <div>
                            <p className="text-sm font-medium text-text-primary">{file.name}</p>
                            <p className="text-xs text-text-muted">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          disabled={submitting}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    
                    {/* Add More Button */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('file-input').click()}
                      disabled={submitting}
                      className="w-full rounded-lg"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add More Files
                    </Button>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Additional Notes (Optional)</Label>
                <Textarea
                  value={notes}
                  disabled={submitting}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information about these certificates..."
                  className="rounded-lg min-h-[80px]"
                  maxLength={500}
                  data-testid="upload-notes"
                />
              </div>

              {/* Upload Progress */}
              {submitting && uploadProgress > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Uploading...</span>
                    <span className="font-medium">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={files.length === 0 || submitting}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl h-12"
                data-testid="submit-upload-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Submit {files.length} Certificate{files.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Help Text */}
        <p className="text-center text-xs text-text-muted mt-6">
          Having trouble? Contact your HR administrator for assistance.
        </p>
      </div>
    </div>
  );
}
